import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQuery } from '@core/api';

export const instancesApi = createApi({
  reducerPath: 'instancesApi',
  baseQuery,
  tagTypes: ['Instances', 'StepResponses'],
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    // Instance endpoints
    getInstances: builder.query({
      query: ({ statusGroup } = {}) => {
        const params = statusGroup ? `?status_group=${statusGroup}` : '';
        return `/api/v1/onboarding/instances/${params}`;
      },
      providesTags: ['Instances'],
    }),
    getInstanceCounts: builder.query({
      query: () => '/api/v1/onboarding/instances/counts/',
      // Same 'Instances' tag type so any instance mutation refetches the counts
      providesTags: [{ type: 'Instances', id: 'COUNTS' }],
    }),
    getInstance: builder.query({
      query: (id) => `/api/v1/onboarding/instances/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Instances', id }],
    }),
    createInstance: builder.mutation({
      query: (data) => ({
        url: '/api/v1/onboarding/instances/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Instances'],
    }),
    cancelInstance: builder.mutation({
      query: (id) => ({
        url: `/api/v1/onboarding/instances/${id}/cancel/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Instances', id },
        'Instances',
      ],
    }),
    getProgress: builder.query({
      query: (id) => `/api/v1/onboarding/instances/${id}/progress/`,
      providesTags: (result, error, id) => [{ type: 'Instances', id }],
    }),
    getInstanceConversation: builder.query({
      query: (id) => `/api/v1/onboarding/instances/${id}/conversation/`,
      providesTags: (result, error, id) => [{ type: 'Instances', id }],
    }),

    // Step Response endpoints
    getStepResponses: builder.query({
      query: () => '/api/v1/onboarding/step-responses/',
      providesTags: ['StepResponses'],
    }),
    getStepResponse: builder.query({
      query: (id) => `/api/v1/onboarding/step-responses/${id}/`,
      providesTags: (result, error, id) => [{ type: 'StepResponses', id }],
    }),
    completeStep: builder.mutation({
      query: ({ id, response_data }) => ({
        url: `/api/v1/onboarding/step-responses/${id}/complete/`,
        method: 'POST',
        body: { response_data },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'StepResponses', id },
        'Instances',
      ],
    }),
    startStep: builder.mutation({
      query: (id) => ({
        url: `/api/v1/onboarding/step-responses/${id}/start/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'StepResponses', id },
        'Instances',
      ],
    }),
    skipStep: builder.mutation({
      query: (id) => ({
        url: `/api/v1/onboarding/step-responses/${id}/skip/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'StepResponses', id },
        'Instances',
      ],
    }),
  }),
});

export const {
  useGetInstancesQuery,
  useGetInstanceCountsQuery,
  useGetInstanceQuery,
  useCreateInstanceMutation,
  useCancelInstanceMutation,
  useGetProgressQuery,
  useGetInstanceConversationQuery,
  useGetStepResponsesQuery,
  useGetStepResponseQuery,
  useCompleteStepMutation,
  useStartStepMutation,
  useSkipStepMutation,
} = instancesApi;
